import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useChatContext } from '@/pages/chat/context';

interface ChartProp {
  option?: any;
}

const CHART_COLOR_COUNT = 12;

const isPlainObject = (value: any) => Object.prototype.toString.call(value) === '[object Object]';

const asArray = (value: any) => (Array.isArray(value) ? value : value ? [value] : []);

const mergeDefaults = (value: any, defaults: any): any => {
  if (Array.isArray(value)) {
    return value.map(item => mergeDefaults(item, defaults));
  }

  if (!isPlainObject(value) || !isPlainObject(defaults)) {
    return value ?? defaults;
  }

  const merged = { ...defaults, ...value };
  Object.keys(defaults).forEach(key => {
    if (value[key] !== undefined) {
      merged[key] = mergeDefaults(value[key], defaults[key]);
    }
  });

  return merged;
};

export const cssVar = (name: string, fallback = '') => {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const createMixedLegendIcon = (type: 'bar' | 'line', color: string) => {
  const width = type === 'bar' ? 12 : 16;
  const height = type === 'bar' ? 12 : 4;
  const x = (16 - width) / 2;
  const y = (12 - height) / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${color}"/></svg>`;
  return `image://data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildThemeOption = () => {
  const textColor = cssVar('--color-text');
  const secondaryTextColor = cssVar('--color-text-secondary');
  const borderColor = cssVar('--color-neutral-4');
  const tooltipBg = cssVar('--message-bg');
  const palette = Array.from({ length: CHART_COLOR_COUNT }, (_, index) =>
    cssVar(`--color-diagram-${index + 1}`),
  ).filter(Boolean);

  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: {
      color: secondaryTextColor,
    },
    title: {
      textStyle: {
        color: textColor,
      },
      subtextStyle: {
        color: secondaryTextColor,
      },
    },
    legend: {
      textStyle: {
        color: textColor,
      },
    },
    tooltip: {
      backgroundColor: tooltipBg,
      borderColor,
      textStyle: {
        color: textColor,
      },
    },
    xAxis: {
      axisLine: {
        lineStyle: {
          color: borderColor,
        },
      },
      axisTick: {
        lineStyle: {
          color: borderColor,
        },
      },
      axisLabel: {
        color: textColor,
      },
      splitLine: {
        lineStyle: {
          color: borderColor,
        },
      },
    },
    yAxis: {
      axisLine: {
        lineStyle: {
          color: borderColor,
        },
      },
      axisTick: {
        lineStyle: {
          color: borderColor,
        },
      },
      axisLabel: {
        color: textColor,
      },
      splitLine: {
        lineStyle: {
          color: borderColor,
        },
      },
    },
  };
};

const formatValue = (value: any) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return value ?? '-';
  return number.toFixed(2);
};

const escapeTooltipText = (value: any) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getTooltipValue = (param: any) => {
  const value = param?.value ?? param?.data?.value;
  if (!Array.isArray(value)) return value;

  const encodedYIndex = asArray(param?.encode?.y)[0];
  if (typeof encodedYIndex === 'number' && value[encodedYIndex] !== undefined) {
    return value[encodedYIndex];
  }

  return [...value].reverse().find(item => Number.isFinite(Number(item))) ?? value[value.length - 1];
};

const getSeriesUnit = (option: any, param: any) => {
  const series = asArray(option?.series)[param?.seriesIndex] || {};
  const yAxisIndex = series.yAxisIndex || 0;
  const yAxis = asArray(option?.yAxis)[yAxisIndex] || {};
  return series.unit || series.metadata?.unit || yAxis.name || option?.unit || option?.metadata?.unit || '';
};

const createCartesianTooltipFormatter = (option: any) => (rawParams: any) => {
  const params = asArray(rawParams);
  if (!params.length) return '';

  const axisValue = params[0]?.axisValue ?? params[0]?.name ?? '';
  const rows = params.map(param => {
    const seriesName = escapeTooltipText(param?.seriesName);
    const value = escapeTooltipText(formatValue(getTooltipValue(param)));
    const unit = escapeTooltipText(getSeriesUnit(option, param));
    return `<div style="display:flex;align-items:center;gap:8px;line-height:24px;">${
      param?.marker || ''
    }<span>${seriesName}</span><span style="margin-left:auto;padding-left:12px;">${value}${unit}</span></div>`;
  });

  return `<div style="min-width:180px;"><div style="margin-bottom:4px;line-height:24px;">${escapeTooltipText(
    axisValue,
  )}</div>${rows.join('')}</div>`;
};

const formatCategoryAxisLabel = (value: any) => {
  const text = String(value ?? '');
  const dateMatch = text.match(/^\d{4}-(\d{2})-(\d{2})/);
  return dateMatch ? `${dateMatch[1]}-${dateMatch[2]}` : text;
};

const mapChartOption = (value: any, mapper: (item: any, index: number) => any) => {
  if (Array.isArray(value)) return value.map(mapper);
  return mapper(value || {}, 0);
};

const firstDefined = (...values: any[]) => values.find(value => value !== undefined && value !== null);

const getPieSliceValue = (item: any) => Number(item?.value || 0);

const getPiePercent = (item: any, total: number) => {
  const explicitPercent = firstDefined(item?.percent, item?.percentage, item?.ratio);
  if (explicitPercent !== undefined) {
    const number = Number(explicitPercent);
    if (!Number.isFinite(number)) return explicitPercent;
    return number <= 1 ? number * 100 : number;
  }

  const value = getPieSliceValue(item);
  return total > 0 ? (value / total) * 100 : 0;
};

const getPieData = (option: any) => {
  const pieSeries = asArray(option?.series).find((item: any) => item?.type === 'pie');
  return Array.isArray(pieSeries?.data) ? pieSeries.data : [];
};

const isPieOption = (option: any) => asArray(option?.series).some((item: any) => item?.type === 'pie');

const isGaugeOption = (option: any) => asArray(option?.series).some((item: any) => item?.type === 'gauge');

const toRgbaColor = (color: string, alpha: number) => {
  const normalizedColor = color?.trim();
  if (!normalizedColor) return `rgba(40,111,220,${alpha})`;

  if (normalizedColor.startsWith('#')) {
    const hex = normalizedColor.slice(1);
    const fullHex =
      hex.length === 3
        ? hex
            .split('')
            .map(char => char + char)
            .join('')
        : hex;

    if (fullHex.length === 6) {
      const red = parseInt(fullHex.slice(0, 2), 16);
      const green = parseInt(fullHex.slice(2, 4), 16);
      const blue = parseInt(fullHex.slice(4, 6), 16);
      return `rgba(${red},${green},${blue},${alpha})`;
    }
  }

  const rgbMatch = normalizedColor.match(/^rgba?\(([^)]+)\)$/);
  if (rgbMatch) {
    const [red, green, blue] = rgbMatch[1].split(',').map(value => value.trim());
    return `rgba(${red},${green},${blue},${alpha})`;
  }

  return normalizedColor;
};

const getSeriesColor = (series: any, option: any, index: number) => {
  const palette = asArray(option?.color);
  return series?.itemStyle?.color || series?.lineStyle?.color || palette[index % palette.length] || '#286fdc';
};

const buildLineOption = (option: any) => {
  const series = asArray(option?.series);
  const hasBarSeries = series.some(item => item?.type === 'bar');
  const hasLineSeries = series.some(item => item?.type === 'line');
  const isLineChart = series.length > 0 && series.every(item => item?.type === 'line');
  const isMixedChart = hasBarSeries && hasLineSeries;
  if (!hasBarSeries && !hasLineSeries) return option;

  const textColor = cssVar('--color-text', '#303133');
  const secondaryTextColor = cssVar('--color-text-secondary', '#606266');
  const mutedTextColor = cssVar('--color-text-muted', '#909499');
  const borderColor = cssVar('--color-neutral-4', '#dcdfe6');
  const styledSeries = series.map((item, index) => {
    if (item?.type === 'bar') {
      return {
        ...item,
        barMaxWidth: 24,
      };
    }

    if (item?.type === 'line') {
      const seriesColor = getSeriesColor(item, option, index);
      return {
        ...item,
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          ...(item.lineStyle || {}),
          width: 2,
        },
        areaStyle: {
          ...(item.areaStyle || {}),
          color: item.areaStyle?.color || toRgbaColor(seriesColor, 0.05),
        },
      };
    }

    return item;
  });

  const buildMixedLegendData = (legend: any) => {
    const sourceData = asArray(legend?.data);
    const legendData = sourceData.length
      ? sourceData
      : styledSeries.map(item => item?.name).filter((name: any) => name !== undefined && name !== null);

    return legendData.map(dataItem => {
      const name = typeof dataItem === 'string' ? dataItem : dataItem?.name;
      const seriesIndex = styledSeries.findIndex(item => item?.name === name);
      const seriesItem = styledSeries[seriesIndex];
      if (seriesItem?.type !== 'bar' && seriesItem?.type !== 'line') return dataItem;

      const seriesColor = getSeriesColor(seriesItem, option, seriesIndex);
      const iconColor = typeof seriesColor === 'string' ? seriesColor : cssVar('--color-primary', '#286fdc');
      const icon = createMixedLegendIcon(seriesItem.type, iconColor);
      return typeof dataItem === 'string' ? { name: dataItem, icon } : { ...dataItem, icon };
    });
  };

  const styleLegend = (legend: any) => {
    const styledLegend = {
      ...(legend || {}),
      show: true,
      type: 'scroll',
      right: 8,
      top: 8,
      itemWidth: isMixedChart || isLineChart ? 16 : 12,
      itemHeight: isLineChart ? 4 : 12,
      itemGap: 16,
      inactiveColor: mutedTextColor,
      pageButtonPosition: 'end',
      pageButtonItemGap: 8,
      pageButtonGap: 12,
      pageIconColor: textColor,
      pageIconInactiveColor: mutedTextColor,
      pageIconSize: 12,
      pageFormatter: '{current}/{total}',
      pageTextStyle: {
        ...(legend?.pageTextStyle || {}),
        color: secondaryTextColor,
        fontSize: 12,
      },
      textStyle: {
        ...(legend?.textStyle || {}),
        color: textColor,
        fontSize: 14,
      },
    };
    if (isMixedChart) {
      styledLegend.data = buildMixedLegendData(legend);
      delete styledLegend.icon;
    } else {
      styledLegend.icon = 'rect';
    }
    return styledLegend;
  };

  const styledOption = {
    ...option,
    backgroundColor: 'transparent',
    title: mapChartOption(option?.title, title => ({
      ...title,
      show: false,
    })),
    grid: {
      top: 60,
      right: 24,
      bottom: 32,
      left: 12,
      containLabel: true,
      ...(option?.grid || {}),
    },
    legend: Array.isArray(option?.legend) ? option.legend.map(styleLegend) : styleLegend(option?.legend),
    tooltip: {
      ...(option?.tooltip || {}),
      trigger: 'axis',
      appendTo: 'body',
      confine: true,
      borderColor,
      borderWidth: 1,
      padding: [8, 12],
      extraCssText: 'border-radius:4px;box-shadow:0 4px 12px rgba(0,0,0,0.24);',
      textStyle: {
        ...(option?.tooltip?.textStyle || {}),
        color: textColor,
        fontSize: 14,
      },
      axisPointer: {
        ...(option?.tooltip?.axisPointer || {}),
        type: 'shadow',
      },
    },
    xAxis: mapChartOption(option?.xAxis, axis => ({
      ...axis,
      type: axis?.type || 'category',
      axisTick: {
        ...(axis?.axisTick || {}),
        show: false,
      },
      axisLine: {
        ...(axis?.axisLine || {}),
        show: true,
        lineStyle: {
          ...(axis?.axisLine?.lineStyle || {}),
          color: borderColor,
        },
      },
      axisLabel: {
        ...(axis?.axisLabel || {}),
        color: textColor,
        hideOverlap: true,
        formatter: formatCategoryAxisLabel,
        textStyle: {
          ...(axis?.axisLabel?.textStyle || {}),
          color: textColor,
        },
      },
      splitLine: {
        ...(axis?.splitLine || {}),
        show: false,
      },
    })),
    yAxis: mapChartOption(option?.yAxis, (axis, index) => ({
      ...axis,
      type: axis?.type || 'value',
      splitNumber: 4,
      nameGap: 12,
      nameTextStyle: {
        ...(axis?.nameTextStyle || {}),
        color: secondaryTextColor,
        fontSize: 12,
        align: index === 0 ? 'right' : 'left',
      },
      axisTick: {
        ...(axis?.axisTick || {}),
        show: false,
      },
      axisLine: {
        ...(axis?.axisLine || {}),
        show: false,
      },
      axisLabel: {
        ...(axis?.axisLabel || {}),
        color: textColor,
        textStyle: {
          ...(axis?.axisLabel?.textStyle || {}),
          color: textColor,
        },
      },
      splitLine: {
        ...(axis?.splitLine || {}),
        show: true,
        lineStyle: {
          ...(axis?.splitLine?.lineStyle || {}),
          color: borderColor,
        },
      },
    })),
    series: styledSeries,
  };

  styledOption.tooltip.formatter = option?.tooltip?.formatter ?? createCartesianTooltipFormatter(styledOption);
  return styledOption;
};

const buildPieOption = (option: any) => {
  const { xAxis, yAxis, grid, ...pieOption } = option || {};
  const textColor = cssVar('--color-text');
  const secondaryTextColor = cssVar('--color-text-secondary');
  const borderColor = cssVar('--color-neutral-4');
  const tooltipBg = cssVar('--message-bg');
  const innerCircleColor = cssVar('--color-diagram-2', '#28bddc');
  const data = getPieData(pieOption);
  const total = data.reduce((sum: number, item: any) => sum + getPieSliceValue(item), 0);
  const unit = pieOption?.unit || pieOption?.metadata?.unit || '';
  const legendShowPercent = pieOption?.legendShowPercent !== false && pieOption?.metadata?.legendShowPercent !== false;
  const legendFontSize = 14;
  const legendNameWidth = 120;
  const measureContext = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  if (measureContext) measureContext.font = `${legendFontSize}px sans-serif`;
  const measureLegendText = (text: string) => {
    if (measureContext) return measureContext.measureText(text).width;
    return Array.from(text).reduce(
      (width, character) => width + (character.charCodeAt(0) > 255 ? legendFontSize : legendFontSize * 0.6),
      0,
    );
  };
  const getColumnWidth = (texts: string[]) =>
    Math.ceil(texts.reduce((width, text) => Math.max(width, measureLegendText(text)), 0) + 30);
  const truncateLegendText = (text: string) => {
    if (measureLegendText(text) <= legendNameWidth) return text;

    const ellipsis = '...';
    const availableWidth = legendNameWidth - measureLegendText(ellipsis);
    let result = '';
    let resultWidth = 0;
    for (const character of Array.from(text)) {
      const characterWidth = measureLegendText(character);
      if (resultWidth + characterWidth > availableWidth) break;
      result += character;
      resultWidth += characterWidth;
    }
    return `${result}${ellipsis}`;
  };
  const getLegendValueText = (item: any) => `${formatValue(item?.value)}${unit ? `${unit}` : ''}`;
  const percentColumnWidth = legendShowPercent
    ? getColumnWidth(data.map((item: any) => `${formatValue(getPiePercent(item, total))}%`))
    : 0;
  const valueColumnWidth = getColumnWidth(data.map(getLegendValueText));
  const pieCenter = ['24%', '50%'];
  const legendShouldScroll = data.length > 6;
  const styledSeries = asArray(pieOption?.series).map((item: any, index: number) => {
    if (item?.type !== 'pie') return item;
    if (index > 0 && !item?.data) return item;

    return {
      ...item,
      type: 'pie',
      center: pieCenter,
      radius: ['46%', '66%'],
      percentPrecision: 2,
      padAngle: 1,
      minAngle: 2,
      label: {
        show: false,
      },
      labelLine: {
        show: false,
      },
      emphasis: {
        scale: true,
        scaleSize: 4,
      },
    };
  });

  const hasInnerCircle = styledSeries.some((item: any) => item?.name === '__inner_circle__');
  if (!hasInnerCircle) {
    styledSeries.push({
      type: 'pie',
      name: '__inner_circle__',
      center: pieCenter,
      radius: ['35%', '45%'],
      silent: true,
      tooltip: {
        show: false,
      },
      label: {
        show: false,
      },
      labelLine: {
        show: false,
      },
      animation: false,
      data: [
        {
          value: total || 1,
          name: '__inner_circle__',
          itemStyle: {
            color: innerCircleColor,
            opacity: 0.16,
          },
        },
      ],
    });
  }

  return {
    ...pieOption,
    title: mapChartOption(pieOption?.title, title => ({
      ...title,
      show: false,
    })),
    tooltip: {
      trigger: 'item',
      backgroundColor: tooltipBg,
      borderColor,
      padding: [8, 12],
      textStyle: {
        color: textColor,
      },
      formatter: (params: any) => {
        const item = data.find((current: any) => current?.name === params.name) || params.data;
        const value = formatValue(item?.value);
        const percent = legendShowPercent ? `&nbsp;&nbsp;${formatValue(getPiePercent(item, total))}%` : '';
        return `${params.marker}${params.name}${percent}&nbsp;&nbsp;${value}${unit}`;
      },
    },
    legend: {
      show: true,
      type: legendShouldScroll ? 'scroll' : 'plain',
      orient: 'vertical',
      top: legendShouldScroll ? 16 : 'center',
      bottom: legendShouldScroll ? 16 : undefined,
      left: '42%',
      right: 12,
      icon: 'circle',
      data: data.map((item: any) => item?.name).filter(Boolean),
      itemGap: 16,
      itemWidth: 8,
      itemHeight: 8,
      pageButtonPosition: 'end',
      pageIconColor: secondaryTextColor,
      pageIconInactiveColor: cssVar('--color-text-muted'),
      pageTextStyle: {
        color: secondaryTextColor,
      },
      textStyle: {
        color: secondaryTextColor,
        fontSize: 14,
        rich: {
          name: {
            width: legendNameWidth,
            color: secondaryTextColor,
            fontSize: legendFontSize,
          },
          percent: {
            width: percentColumnWidth,
            align: 'right',
            color: secondaryTextColor,
            fontSize: legendFontSize,
          },
          value: {
            width: valueColumnWidth,
            align: 'right',
            color: secondaryTextColor,
            fontSize: legendFontSize,
          },
        },
      },
      formatter: (name: string) => {
        const item = data.find((current: any) => current?.name === name);
        const percent = getPiePercent(item, total);
        const percentText = legendShowPercent ? `{percent|${formatValue(percent)}%}` : '';
        const valueText = getLegendValueText(item);
        return `{name|${truncateLegendText(name)}}${percentText}{value|${valueText}}`;
      },
    },
    series: styledSeries,
  };
};

const applyChartTheme = (option: any) => {
  if (isPieOption(option)) {
    return buildPieOption(
      mergeDefaults(option || {}, {
        color: buildThemeOption().color,
        backgroundColor: 'transparent',
      }),
    );
  }

  const themeOption = buildThemeOption();
  if (isGaugeOption(option)) {
    const { xAxis, yAxis, ...gaugeThemeOption } = themeOption;
    return mergeDefaults(option || {}, gaugeThemeOption);
  }

  const themedOption = mergeDefaults(option || {}, themeOption);
  return buildLineOption(themedOption);
};

const Chart = ({ option = {} }: ChartProp) => {
  const { theme } = useChatContext();
  const chartOption = useMemo(() => applyChartTheme(option), [option, theme]);

  return (
    <div className="my-2 min-w-[480px]" style={{ background: 'var(--color-neutral-2)', borderRadius: 8 }}>
      <ReactEcharts style={{ height: 240 }} option={chartOption} notMerge lazyUpdate={false} />
    </div>
  );
};

export default Chart;
